// ไฟล์: frontend/src/pages/TeacherProfilePage.js
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "./TeacherProfilePage.css";

const TeacherProfilePage = ({ user }) => {
  const { id } = useParams();
  const [teacher, setTeacher] = useState(null);
  const [stats, setStats] = useState({});
  const [badges, setBadges] = useState([]);
  const [latestContent, setLatestContent] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeacherProfile = async () => {
      try {
        const response = await axios.get(`/teachers/${id}`);
        setTeacher(response.data.teacher);
        setStats(response.data.stats);
        setBadges(response.data.badges);
        setLatestContent(response.data.latestContent);
        setLoading(false);

        // ตรวจสอบสถานะการติดตาม (ถ้ามีการล็อกอิน)
        if (user) {
          try {
            const followResponse = await axios.get(`/follow/status/${id}`);
            setIsFollowing(followResponse.data.followed);
          } catch (error) {
            console.error("Error checking follow status:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching teacher profile:", error);
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "ไม่พบข้อมูล",
          text: "ไม่พบข้อมูลครูที่ต้องการ",
        });
      }
    };

    fetchTeacherProfile();
  }, [id, user]);

  const handleFollow = async () => {
    if (!user) {
      Swal.fire({
        icon: "info",
        title: "กรุณาเข้าสู่ระบบ",
        text: "คุณต้องเข้าสู่ระบบก่อนจึงจะสามารถติดตามครูได้",
        showCancelButton: true,
        confirmButtonText: "เข้าสู่ระบบ",
        cancelButtonText: "ยกเลิก",
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = "/login";
        }
      });
      return;
    }

    try {
      const response = await axios.post("/follow", { teacherId: id });
      setIsFollowing(response.data.followed);

      Swal.fire({
        icon: "success",
        title: response.data.followed
          ? "ติดตามสำเร็จ"
          : "ยกเลิกการติดตามสำเร็จ",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error following teacher:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถดำเนินการได้ในขณะนี้ โปรดลองอีกครั้งในภายหลัง",
      });
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        {/* <div className="loading-spinner">กำลังโหลดข้อมูล...</div> */}
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className="not-found-container">
        <h2>ไม่พบข้อมูลครู</h2>
        <p>ไม่พบข้อมูลครูที่คุณต้องการ</p>
        <Link to="/" className="btn btn-primary">
          กลับสู่หน้าหลัก
        </Link>
      </div>
    );
  }

  return (
    <div className="teacher-profile-page">
      <div className="container">
        {/* Teacher Profile Header */}
        <div className="profile-header">
          <div className="profile-header-content">
            <div className="profile-avatar">
              <img
                src={
                  teacher.profile_image
                    ? `${process.env.REACT_APP_API_URL}/uploads/profiles/${teacher.profile_image}`
                    : `https://ui-avatars.com/api/?name=${teacher.fullname}&background=random&size=300`
                }
                alt={teacher.fullname}
              />
            </div>
            <div className="profile-info">
              <h1 className="profile-name">{teacher.fullname}</h1>
              <div className="profile-meta">
                <div className="profile-meta-item">
                  <i className="fas fa-school"></i>{" "}
                  {teacher.school || "ไม่ระบุโรงเรียน"}
                </div>
                <div className="profile-meta-item">
                  <i className="fas fa-book"></i>{" "}
                  {teacher.subject_area || "ไม่ระบุกลุ่มสาระ"}
                </div>
                <div className="profile-meta-item">
                  <i className="fas fa-calendar-alt"></i> เข้าร่วมเมื่อ{" "}
                  {new Date(teacher.created_at).toLocaleDateString("th-TH")}
                </div>
              </div>
              <div className="profile-stats">
                <div className="profile-stat">
                  <div className="stat-value">{stats.followers}</div>
                  <div className="stat-label">ผู้ติดตาม</div>
                </div>
                <div className="profile-stat">
                  <div className="stat-value">{stats.totalContent}</div>
                  <div className="stat-label">ผลงานทั้งหมด</div>
                </div>
                <div className="profile-stat">
                  <div className="stat-value">{stats.resources}</div>
                  <div className="stat-label">สื่อการสอน</div>
                </div>
                <div className="profile-stat">
                  <div className="stat-value">{stats.innovations}</div>
                  <div className="stat-label">นวัตกรรม</div>
                </div>
                <div className="profile-stat">
                  <div className="stat-value">{stats.articles}</div>
                  <div className="stat-label">บทความ</div>
                </div>
              </div>
              {teacher.bio && (
                <div className="profile-bio">
                  <h3>เกี่ยวกับครู</h3>
                  <p>{teacher.bio}</p>
                </div>
              )}
              {user && user.id !== parseInt(id) && (
                <div className="profile-actions">
                  <button
                    className={`btn ${
                      isFollowing ? "btn-light" : "btn-primary"
                    }`}
                    onClick={handleFollow}
                  >
                    <i
                      className={`fas ${
                        isFollowing ? "fa-user-minus" : "fa-user-plus"
                      }`}
                    ></i>
                    {isFollowing ? " ยกเลิกการติดตาม" : " ติดตาม"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="profile-content">
          <div className="profile-main">
            {/* Latest Content */}
            <div className="content-section">
              <div className="section-header">
                <h2>ผลงานล่าสุด</h2>
              </div>
              {latestContent.length > 0 ? (
                <div className="resources-grid">
                  {latestContent.map((content) => (
                    <div
                      className="resource-card"
                      key={`${content.type}-${content.id}`}
                    >
                      <div className="resource-thumbnail">
                        <img
                          src={
                            content.thumbnail
                              ? `${process.env.REACT_APP_API_URL}/uploads/${content.thumbnail}`
                              : "/assets/default-resource.jpg"
                          }
                          alt={content.title}
                        />
                        <span className="resource-type">
                          {content.type === "resource" && (
                            <i className="fas fa-book"></i>
                          )}
                          {content.type === "innovation" && (
                            <i className="fas fa-lightbulb"></i>
                          )}
                          {content.type === "article" && (
                            <i className="fas fa-pen-fancy"></i>
                          )}
                          {content.type}
                        </span>
                      </div>
                      <div className="resource-content">
                        <h3 className="resource-title">
                          <Link to={`/${content.type}s/${content.id}`}>
                            {content.title}
                          </Link>
                        </h3>
                        <div className="resource-meta">
                          <div className="resource-date">
                            <i className="far fa-calendar-alt"></i>{" "}
                            {new Date(content.created_at).toLocaleDateString(
                              "th-TH"
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="resource-footer">
                        <Link
                          to={`/${content.type}s/${content.id}`}
                          className="btn btn-sm btn-primary"
                        >
                          <i className="fas fa-info-circle"></i> รายละเอียด
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-content">
                  <i className="fas fa-inbox"></i>
                  <p>ยังไม่มีผลงานที่เผยแพร่</p>
                </div>
              )}
            </div>
          </div>

          <div className="profile-sidebar">
            {/* Badges */}
            <div className="sidebar-card">
              <div className="sidebar-card-title">
                <i className="fas fa-award"></i> เหรียญรางวัล
              </div>
              {badges.length > 0 ? (
                <div className="badges-grid">
                  {badges.map((badge) => (
                    <div className="badge-item" key={badge.id}>
                      <div className="badge-icon">
                        <i className={`fas fa-${badge.icon}`}></i>
                      </div>
                      <div className="badge-info">
                        <div className="badge-name">{badge.name}</div>
                        <div className="badge-date">
                          ได้รับเมื่อ{" "}
                          {new Date(badge.earned_at).toLocaleDateString(
                            "th-TH"
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-content small">
                  <p>ยังไม่มีเหรียญรางวัล</p>
                </div>
              )}
            </div>

            {/* Resources */}
            <div className="sidebar-card">
              <div className="sidebar-card-title">
                <i className="fas fa-book"></i> สื่อการสอน
              </div>
              <div className="sidebar-links">
                <Link
                  to={`/search?teacher=${id}&type=resource`}
                  className="sidebar-link"
                >
                  <i className="fas fa-chevron-right"></i> ดูสื่อการสอนทั้งหมด (
                  {stats.resources})
                </Link>
              </div>
            </div>

            {/* Innovations */}
            <div className="sidebar-card">
              <div className="sidebar-card-title">
                <i className="fas fa-lightbulb"></i> นวัตกรรม
              </div>
              <div className="sidebar-links">
                <Link
                  to={`/search?teacher=${id}&type=innovation`}
                  className="sidebar-link"
                >
                  <i className="fas fa-chevron-right"></i> ดูนวัตกรรมทั้งหมด (
                  {stats.innovations})
                </Link>
              </div>
            </div>

            {/* Articles */}
            <div className="sidebar-card">
              <div className="sidebar-card-title">
                <i className="fas fa-pen-fancy"></i> บทความ
              </div>
              <div className="sidebar-links">
                <Link
                  to={`/search?teacher=${id}&type=article`}
                  className="sidebar-link"
                >
                  <i className="fas fa-chevron-right"></i> ดูบทความทั้งหมด (
                  {stats.articles})
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherProfilePage;
